import React, { useEffect, useRef, useState } from 'react';
import api from '../../../services/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../../components/ui/sheet';

const btn = 'mt-2 w-full rounded-full bg-neutral-950 py-2 text-xs font-bold text-white disabled:opacity-50';
const actionStyle = { background:'#0B0F0C', color:'#fff' };
const pending = 'Password reset pending. Your saved password has not changed.';
export default function MasterPasswordReset({ id, gameName, legacy, refresh, managed }) {
  const path = '/api/games/accounts/' + encodeURIComponent(id) + '/master-password';
  const [mode, setMode] = useState('hidden'), [short,setShort] = useState(false);
  const [open,setOpen] = useState(false), [password,setPassword] = useState('');
  const [state,setState] = useState('idle'), [message,setMessage] = useState(''), [busy,setBusy] = useState(false);
  const [delay,setDelay] = useState(5);
  const key = useRef(null), lock = useRef(false), mounted = useRef(false), refreshRef = useRef(refresh);
  const tracking = useRef(null);
  refreshRef.current = refresh;
  useEffect(() => {
    if(managed===false) return;
    let active = true;
    mounted.current = true;
    api.get(path).then(({data}) => {
      if (!active) return;
      setMode(data.mode); setShort(Boolean(data.short)); setState(data.status || 'idle');
      tracking.current=data.tracking||null;
      if (data.status) setMessage(data.status==='unknown'?'Reset result unconfirmed—contact support':pending);
    }).catch(e => { if (active) setMode([401,403].includes(e.response?.status)?'restricted':'hidden'); });
    return () => { active=false; mounted.current=false; };
  }, [path,managed]);
  const checkRef=useRef(null);
  async function act(check) {
    if (lock.current) return;
    lock.current=true; setBusy(true);
    try {
      if (!check && !key.current) key.current=crypto.randomUUID();
      const {data}=await api.post(check ? path+'/check' : path,check?{}:{new_password:password},{
        timeout:255000,headers:check ? (tracking.current?{'X-Reset-Tracking':tracking.current}:key.current?{'X-Reset-Intent':key.current}:{}) : {'X-Idempotency-Key':key.current},
      });
      if (!mounted.current) return;
      setState(data.status); setMessage(data.message); setDelay(Math.max(3,data.retry_after||5));
      if (data.status!=='idle') setPassword('');
      if (data.status==='succeeded') { key.current=null; tracking.current=null; refreshRef.current?.(); }
      if (data.status==='failed') { key.current=null; tracking.current=null; }
    } catch(e) {
      if (!mounted.current) return;
      const code=e.response?.status;
      const detail=e.response?.data?.detail || e.response?.data?.message;
      if (code===409 && detail==='Another password reset is already open. Check its status.') key.current=null;
      if (!code || code>=500 || code===429 || code===409 && detail==='Another password reset is already open. Check its status.') {
        setState('pending'); setMessage(code===429?'Master is busy. Checking again shortly.':pending);
        setDelay(Math.max(10,Number(e.response?.headers?.['retry-after'])||10));
      } else {
        setMessage(detail || 'Could not check this reset. Contact support.');
        setState(code===401 || code===403 ? 'access' : 'idle');
      }
    } finally { lock.current=false; if (mounted.current) setBusy(false); }
  }
  checkRef.current=()=>act(true);
  useEffect(()=>{
    if (!open || state!=='pending' || busy) return;
    const timer=setTimeout(()=>checkRef.current(),Math.min(delay,86400)*1000);
    return ()=>clearTimeout(timer);
  },[open,state,busy,delay]);
  if (managed===false || mode==='support') return legacy;
  if (mode==='restricted') return <p className="mt-2 text-sm text-neutral-700">This team does not have access to game password resets. Contact support.</p>;
  if (mode!=='master' && mode!=='tracking') return null;
  const editable=mode==='master' && ['idle','failed'].includes(state);
  const hint=short?'6–16 letters or numbers.':'8–20 characters: letters, numbers or !@#_$%&*+-.';
  const valid=(short?/^[A-Za-z0-9]{6,16}$/:/^[A-Za-z0-9!@#_$%&*+\-]{8,20}$/).test(password);
  return <>
    <button type="button" className={btn} style={actionStyle} onClick={()=>{ if(mode==='master' && state==='succeeded'){setState('idle');setMessage('');} setOpen(true); }}>{mode==='tracking'?'View password reset status':'Reset Password'}</button>
    <Sheet open={open} onOpenChange={v=>{setOpen(v);if(!v)setPassword('');}}>
      <SheetContent side="bottom" showClose={false} style={{background:'#fff',color:'#0B0F0C',maxWidth:560,marginInline:'auto'}} className="player-app rounded-t-3xl bg-white p-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-neutral-950">
        <SheetHeader><SheetTitle style={{color:'#0B0F0C'}}>Reset {gameName} password</SheetTitle></SheetHeader>
        <form onSubmit={e=>{e.preventDefault();if(editable && valid)act(false);}}>
          {editable && <label className="mt-4 block font-semibold">New game password
            <input type="password" autoComplete="new-password" disabled={busy} value={password} onChange={e=>setPassword(e.target.value)}
              aria-describedby={'pw-hint-'+id} style={{background:'#fff',color:'#0B0F0C',borderColor:'#64748B'}} maxLength={short?16:20} className="mt-2 block w-full rounded-lg border border-neutral-400 px-3 py-2 text-base"/>
            <span id={'pw-hint-'+id} className="mt-1 block text-sm font-normal text-neutral-600">{hint}</span>
          </label>}
          <p role="status" className="my-3 text-sm">{busy?'Checking with the game provider…':message}</p>
          {editable && <button className={btn} style={actionStyle} disabled={busy || !valid}>Reset game password</button>}
          {state==='access' && <button className={btn} style={actionStyle} type="button" disabled={busy} onClick={()=>act(true)}>Check existing reset</button>}
          <button className="mt-2 w-full py-2 font-semibold" type="button" onClick={()=>{setOpen(false);setPassword('');}}>Close</button>
        </form>
      </SheetContent>
    </Sheet>
  </>;
}
